import cameraMascot from "@assets/images/camera.png";
import { useAuthStore } from "@features/authentication/stores/authStore";
import { useUpdateOnboardingUser } from "@features/onboarding/mutations/useUpdateOnboardingUser";
import {
  clearPendingPhotoUrl,
  loadPendingPhotoUrl,
  savePendingPhotoUrl,
} from "@features/onboarding/utils/pendingPhotoStorage";
import logger from "@logger";
import { PhotoUploadField } from "@modules/photoUpload";
import { useCurrentUser } from "@shared/queries";
import clsx from "clsx";
import { useEffect, useState } from "react";

interface PhotoUploadFormProps {
  onSaved?: () => void;
}

export default function PhotoUploadForm({ onSaved }: PhotoUploadFormProps) {
  const userId = useAuthStore((state) => state.userId);
  const { user } = useCurrentUser();
  const { mutate: updateOnboardingUser, isPending: isSubmitting, error: mutationError } = useUpdateOnboardingUser();
  const [uploadedPhotoUrl, setUploadedPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      return;
    }
    const pendingUrl = loadPendingPhotoUrl(userId);
    if (pendingUrl) {
      setUploadedPhotoUrl(pendingUrl);
      return;
    }
    if (user?.photo_url) {
      setUploadedPhotoUrl(user.photo_url);
    }
  }, [userId, user?.photo_url]);

  const handleUploadComplete = (photoUrl: string) => {
    logger.info("Photo uploaded successfully", photoUrl);
    setUploadedPhotoUrl(photoUrl);
    if (userId) {
      savePendingPhotoUrl(userId, photoUrl);
    }
  };

  const handleUploadError = (error: Error) => {
    logger.error("Photo upload failed", error);
  };

  const handleFormSubmit = () => {
    if (!uploadedPhotoUrl || isSubmitting || !userId) {
      return;
    }

    logger.info("PhotoUploadForm submitting data:", { photo_url: uploadedPhotoUrl });
    updateOnboardingUser(
      { photo_url: uploadedPhotoUrl },
      {
        onSuccess: () => {
          clearPendingPhotoUrl(userId);
          onSaved?.();
        },
        onError: (error) => {
          logger.error("PhotoUploadForm mutation failed:", error);
        },
      }
    );
  };

  const isContinueDisabled = !uploadedPhotoUrl || isSubmitting;

  return (
    <div data-testid="photo-upload-form-root" className="h-full min-h-0 flex flex-col text-center font-mono">
      <div data-testid="photo-upload-form-content" className="flex-1 overflow-y-auto space-y-4 pb-2">
        <h1 data-testid="photo-upload-form-heading" className="text-2xl font-bold">
          Let's put a face to your name!
        </h1>

        <div data-testid="photo-upload-form-body" className="mx-auto w-full space-y-4">
          {mutationError && (
            <div data-testid="photo-upload-form-error" className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p data-testid="photo-upload-form-error-message" className="text-red-600 text-sm">
                {mutationError.message}
              </p>
            </div>
          )}

          <div data-testid="photo-upload-form-upload-section" className="pb-2">
            <PhotoUploadField
              onUploadComplete={handleUploadComplete}
              onUploadError={handleUploadError}
              emptyStateIcon={
                <img
                  data-testid="photo-upload-form-mascot-image"
                  src={cameraMascot}
                  alt="Camera mascot"
                  width="200"
                  height="200"
                  className="w-36 h-36 object-contain"
                />
              }
            />
          </div>
        </div>

        <p data-testid="photo-upload-form-description" className="text-black/80 text-md">
          You only get one, make sure it's your best!
        </p>
      </div>

      <div data-testid="photo-upload-form-footer" className="sticky bottom-0 z-10 bg-lavender-400 pt-4">
        <button
          type="button"
          disabled={isContinueDisabled}
          onClick={handleFormSubmit}
          className={clsx("w-full px-8 py-4 text-lg rounded-lg font-medium transition-colors", {
            "bg-pink-600 text-white hover:bg-pink-700": !isContinueDisabled,
            "bg-gray-300 text-gray-500 cursor-not-allowed": isContinueDisabled,
          })}
          data-testid="submit-button"
        >
          <span data-testid="photo-upload-form-submit-label">{isSubmitting ? "Saving..." : "Continue"}</span>
        </button>
      </div>
    </div>
  );
}
